import React, { useMemo, useState } from "react";
import {
  RoomService,
  DoneAll,
  CheckCircle,
  Timer,
  TableRestaurant,
  Storefront,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useGetRestaurantTicketsQuery,
  useUpdateItemStatusMutation,
} from "../Store/Services/CartService";
import { msSince, parseApiDate } from "../Utils/apiDate";
import styles from "./ServerPage.module.css";

const STATION_STORAGE_KEY = "pos:server-stations";

const loadSelectedStations = () => {
  try {
    const raw = localStorage.getItem(STATION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const timeAgo = (iso) => {
  if (!iso) return "";
  // msSince handles the API's "YYYY-MM-DD HH:mm:ss.S" timestamps, which
  // new Date() either misreads as local time or rejects outright.
  const diff = Math.floor(msSince(iso) / 60000);
  if (diff <= 0) return "just now";
  return `${diff} min`;
};

const getStatusCode = (line) => String(line?.statusCode || "").toUpperCase();
const stationOf = (line) =>
  line?.prodCatDisplayName || line?.prodCatName || "General";

const isCollectable = (line) => {
  const code = getStatusCode(line);
  return code === "READY" || code === "SERVING";
};

const ServerPage = () => {
  const payload = useMemo(
    () => ({
      statusIds: [4, 5],
      categoryIds: [],
      tableIds: [],
    }),
    []
  );
  const { data, isLoading, isFetching } = useGetRestaurantTicketsQuery(payload);
  const [updateItemStatus] = useUpdateItemStatusMutation();

  const [selectedStations, setSelectedStations] = useState(loadSelectedStations);

  const persistStations = (next) => {
    try {
      localStorage.setItem(STATION_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
  };

  const toggleStation = (station) => {
    setSelectedStations((prev) => {
      const next = prev.includes(station)
        ? prev.filter((s) => s !== station)
        : [...prev, station];
      persistStations(next);
      return next;
    });
  };

  const clearStations = () => {
    setSelectedStations([]);
    persistStations([]);
  };

  const tickets = Array.isArray(data?.tickets) ? data.tickets : [];

  // Distinct stations across all collectable items (for the filter tabs).
  const stations = useMemo(() => {
    const set = new Set();
    tickets.forEach((ticket) => {
      (ticket.items || []).forEach((line) => {
        if (isCollectable(line)) set.add(stationOf(line));
      });
    });
    return Array.from(set).sort();
  }, [tickets]);

  const stationFilterActive = selectedStations.length > 0;

  // Group collectable server items per table, honoring the station filter,
  // and break each table down by pickup station.
  const readyByTable = useMemo(() => {
    const grouped = {};
    tickets.forEach((ticket) => {
      const lines = (ticket.items || []).filter(
        (line) =>
          isCollectable(line) &&
          (!stationFilterActive || selectedStations.includes(stationOf(line)))
      );
      if (lines.length === 0) return;

      const byStation = {};
      lines.forEach((line) => {
        const station = stationOf(line);
        if (!byStation[station]) byStation[station] = [];
        byStation[station].push(line);
      });
      const stationGroups = Object.keys(byStation)
        .sort()
        .map((station) => ({ station, lines: byStation[station] }));

      grouped[ticket.tableId] = {
        tableId: ticket.tableId,
        tableName: ticket.tableName,
        tableType: ticket.tableType,
        orderHedId: ticket.orderHedId,
        lines,
        stationGroups,
        readyAt: lines.reduce(
          (earliest, line) =>
            !earliest ||
            parseApiDate(line.serverAcceptedAt || line.readyAt) <
              parseApiDate(earliest)
              ? line.serverAcceptedAt || line.readyAt
              : earliest,
          null
        ),
      };
    });
    return Object.values(grouped).sort(
      (a, b) => msSince(b.readyAt) - msSince(a.readyAt)
    );
  }, [tickets, selectedStations, stationFilterActive]);

  const totalReadyItems = readyByTable.reduce(
    (sum, group) => sum + group.lines.reduce((s, l) => s + l.qty, 0),
    0
  );

  const updateStatus = async (line, statusId) => {
    if (!line?.orderDetId) {
      return true;
    }

    try {
      await updateItemStatus({
        orderDetId: line.orderDetId,
        statusId,
      }).unwrap();
      return true;
    } catch {
      toast.error("Unable to update item status", {
        position: "bottom-right",
      });
      return false;
    }
  };

  const acceptLine = async (tableId, line) => {
    const ok = await updateStatus(line, 5);
    if (!ok) return;
  };

  const serveLine = async (tableId, line) => {
    const ok = await updateStatus(line, 6);
    if (!ok) return;
  };

  const serveStation = (tableId, lines) => {
    lines.filter(isCollectable).forEach((line) => {
      serveLine(tableId, line);
    });
  };

  const serveAll = (group) => serveStation(group.tableId, group.lines);

  const acceptAllInGroup = (group) => {
    group.lines
      .filter((line) => getStatusCode(line) === "READY")
      .forEach((line) => {
        acceptLine(group.tableId, line);
      });
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <RoomService /> Server Station
          </h1>
          <p className={styles.subtitle}>
            {isLoading || isFetching
              ? "Loading server station..."
              : `${totalReadyItems} item${totalReadyItems === 1 ? "" : "s"} ready to collect · ${readyByTable.length} table${readyByTable.length === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      {stations.length > 1 && (
        <div className={styles.stationTabs}>
          <button
            className={`${styles.stationTab} ${
              !stationFilterActive ? styles.stationTabActive : ""
            }`}
            onClick={clearStations}
          >
            All stations
          </button>
          {stations.map((station) => (
            <button
              key={station}
              className={`${styles.stationTab} ${
                selectedStations.includes(station)
                  ? styles.stationTabActive
                  : ""
              }`}
              onClick={() => toggleStation(station)}
            >
              {station}
            </button>
          ))}
        </div>
      )}

      {readyByTable.length === 0 ? (
        <div className={styles.emptyState}>
          <DoneAll className={styles.emptyIcon} />
          <p>Nothing to collect right now. You're all caught up!</p>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {readyByTable.map((group) => {
            const hasReady = group.lines.some(
              (line) => getStatusCode(line) === "READY"
            );
            const hasServing = group.lines.some(
              (line) => getStatusCode(line) === "SERVING"
            );
            const multiStation = group.stationGroups.length > 1;
            return (
              <div key={group.tableId} className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.cardHeadLeft}>
                    <span className={styles.cardTable}>
                      <TableRestaurant fontSize="small" /> Table{" "}
                      {group.tableName || group.tableId}
                    </span>
                    <span className={styles.cardMeta}>
                      {group.tableType || ""} · #{group.orderHedId}
                    </span>
                  </div>
                  <span className={styles.cardTime}>
                    <Timer fontSize="inherit" /> {timeAgo(group.readyAt)}
                  </span>
                </div>

                {/* Pickup route: which stations to collect this table's food from */}
                <div className={styles.pickupRoute}>
                  <Storefront
                    fontSize="inherit"
                    className={styles.pickupIcon}
                  />
                  <span className={styles.pickupLabel}>Collect from:</span>
                  {group.stationGroups.map((sg) => (
                    <span key={sg.station} className={styles.pickupChip}>
                      {sg.station}
                      <span className={styles.pickupCount}>
                        {sg.lines.reduce((s, l) => s + l.qty, 0)}
                      </span>
                    </span>
                  ))}
                </div>

                <div className={styles.cardBody}>
                  {group.stationGroups.map((sg) => (
                    <div key={sg.station} className={styles.stationBlock}>
                      {multiStation && (
                        <div className={styles.stationBlockHead}>
                          <span className={styles.stationBlockName}>
                            <Storefront fontSize="inherit" /> {sg.station}
                          </span>
                          <button
                            className={styles.stationCollectBtn}
                            disabled={!sg.lines.some(isCollectable)}
                            onClick={() =>
                              serveStation(group.tableId, sg.lines)
                            }
                          >
                            Collect all
                          </button>
                        </div>
                      )}
                      {sg.lines.map((line) => (
                        <div key={line.orderDetId} className={styles.line}>
                          <div className={styles.lineInfo}>
                            <span className={styles.lineQty}>{line.qty}×</span>
                            <span className={styles.lineName}>
                              {line.title}
                            </span>
                            {!multiStation && (
                              <span className={styles.lineStation}>
                                {sg.station}
                              </span>
                            )}
                            {getStatusCode(line) === "READY" ? (
                              <span className={styles.readyTag}>
                                <CheckCircle fontSize="inherit" /> Ready
                              </span>
                            ) : (
                              <span className={styles.inProgressTag}>
                                In Progress
                              </span>
                            )}
                          </div>
                          {getStatusCode(line) === "READY" ? (
                            <div className={styles.lineActionGroup}>
                              <button
                                className={styles.acceptBtn}
                                onClick={() => acceptLine(group.tableId, line)}
                              >
                                Accept
                              </button>
                              <button
                                className={styles.serveBtn}
                                onClick={() => serveLine(group.tableId, line)}
                              >
                                Served
                              </button>
                            </div>
                          ) : (
                            <button
                              className={styles.serveBtn}
                              onClick={() => serveLine(group.tableId, line)}
                            >
                              Served
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div className={styles.actionRow}>
                  <button
                    className={styles.acceptAllBtn}
                    disabled={!hasReady}
                    onClick={() => acceptAllInGroup(group)}
                  >
                    <DoneAll fontSize="small" /> Accept All
                  </button>
                  <button
                    className={styles.serveAllBtn}
                    disabled={!hasServing && !hasReady}
                    onClick={() => serveAll(group)}
                  >
                    <RoomService fontSize="small" /> Mark All Served
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServerPage;
